import React, { type ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUrlSearch } from '../hooks/useUrlState.ts';

const keyFor = (pathname: string) => `filters:${pathname}`;

// Session storage can be blocked; the lists then simply start fresh.
const read = (pathname: string) => {
  try {
    return sessionStorage.getItem(keyFor(pathname)) || '';
  } catch {
    return '';
  }
};

const write = (pathname: string, search: string) => {
  try {
    if (search) sessionStorage.setItem(keyFor(pathname), search);
    else sessionStorage.removeItem(keyFor(pathname));
  } catch {
    // Nothing to do.
  }
};

/**
 * Remembers each list's page, search and filters (the part of the address
 * after `?`) for this browser tab. Coming back to a list through the menu or
 * a back arrow, with a bare address, puts them back; clearing them on the
 * list itself forgets them.
 */
export const FilterMemory = ({ children }: { children: ReactNode }) => {
  const { pathname, hash } = useLocation();
  const search = useUrlSearch();
  const previous = useRef<string | null>(null);
  const arrived = previous.current !== pathname;
  const remembered = arrived && !search ? read(pathname) : '';

  useEffect(() => {
    previous.current = pathname;
    if (search) write(pathname, search);
    // An empty address on arrival is a visit; later, it is a reset.
    else if (!arrived) write(pathname, '');
  }, [pathname, search, arrived]);

  if (remembered) {
    return <Navigate to={{ pathname, search: remembered, hash }} replace />;
  }
  return <>{children}</>;
};

export default FilterMemory;
